import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useService } from '@old/hooks/use-service';
import { useEffectOnUpdate } from '@old/hooks/core';
import { TotalsDevices } from '@old/features/report-devices/tablet-devices';
import DashboardDevices from '@old/features/report-devices/dashboard-devices';
import { boxCircularProgressBarStyle, circularProgressBar } from '@old/styles/generic-styles';
import { useState, useEffect, ChangeEvent } from 'react';
import SearchIcon from '@old/assets/svg/search.svg';
import i18n from '../../libs/language';
import DeleteIcon from '@old/assets/svg/core/trash-red.svg';
import { useGlobalStore } from '@old/global-store/use-store';
import { silobagOptionListService } from '@old/services/domain/page/report/device/silobag-option-list';
import { countrysideOptionListService } from '@old/services/domain/page/report/device/countryside-option-list';
import { Selector } from '@old/ui/selector/selector';
import { deviceListService } from '@old/services/domain/page/report/device/report-device-info';

type InputChg = ChangeEvent<HTMLInputElement>;
type SilobagOptionType = Awaited<ReturnType<typeof silobagOptionListService>>['summary'];
type CountrysideOptionType = Awaited<ReturnType<typeof countrysideOptionListService>>['summary'];

export function ReportDevices() {
  const [deviceName, setDeviceName] = useState('');
  const [filteredSilobags, setFilteredSilobags] = useState<SilobagOptionType | undefined>([]);
  const [filteredCountrysides, setCountrysides] = useState<CountrysideOptionType | undefined>([]);

  const { organizationId, filtersReportDevices, setFiltersReportDevices } = useGlobalStore();

  // eslint-disable-next-line
  const { paginationModel, ...restFilters } = filtersReportDevices;
  const hasFilters = Object.values(restFilters).some((s) => s !== '');

  const {
    trigger: getDeviceReportData,
    summary: deviceReportData,
    isLoading: deviceLoading,
  } = useService(deviceListService, {
    fetchOnMount: true,
  });
  const {
    summary: silobagsDevicesData,
    isLoading: silobagsLoading,
    trigger: getSilobags,
  } = useService(silobagOptionListService, {
    fetchOnMount: [{ query: { organization_id: organizationId } }],
  });

  const {
    summary: countrysidesDevicesData,
    isLoading: countrysidesLoading,
    trigger: getCountrysides,
  } = useService(countrysideOptionListService, {
    fetchOnMount: [{ query: { organization_id: organizationId } }],
  });

  useEffect(() => {
    setFilteredSilobags(silobagsDevicesData);
    setCountrysides(countrysidesDevicesData);
  }, [silobagsLoading, countrysidesLoading]);

  useEffect(() => {
    if (organizationId) {
      getDeviceReportData();
      getSilobags({ query: { organization_id: organizationId } });
      getCountrysides({ query: { organization_id: organizationId } });
    }
  }, [organizationId]);

  useEffectOnUpdate(() => {
    getDeviceReportData();
  }, [deviceName, filtersReportDevices]);

  // TODO: AGREGAR FUNCIONABILIDAD CUANDO ESTÉ EL ENDPOINT
  const handleSearchDevice = (e: InputChg) => {
    const selectedValue = e.target.value;
    setFiltersReportDevices({ ...filtersReportDevices, lance_number: selectedValue });
  };

  //const handleSelectSilobagInstalled = (e: SelectChangeEvent) => {
  const handleSelectSilobagInstalled = (e: SelectChangeEvent<boolean>) => {
    const selectedValue = e.target.value;
    setFiltersReportDevices({ ...filtersReportDevices, installed: selectedValue });
  };

  //Functions Selects Params
  //const handleSelectCountryside = (event: SelectChangeEvent) => {
  const handleSelectCountryside = (event: SelectChangeEvent<number>) => {
    const selectedValue = event.target.value;
    setFiltersReportDevices({ ...filtersReportDevices, countrysideId: selectedValue, silobagId: '' });
  };

  //const handleSelectSilobag = (event: SelectChangeEvent) => {
  const handleSelectSilobag = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    setFiltersReportDevices({ ...filtersReportDevices, silobagId: selectedValue });
  };

  //const handleSelectReport = (event: SelectChangeEvent) => {
  const handleSelectReport = (event: SelectChangeEvent<boolean>) => {
    const selelectedValue = event.target.value;
    setFiltersReportDevices({ ...filtersReportDevices, report: selelectedValue });
  };

  type SilobagOptionListType = Awaited<ReturnType<typeof silobagOptionListService>>['summary'];
  type SilobagListType = SilobagOptionListType[0];

  const siloByCountrysideId =
    filteredSilobags && filteredSilobags.length > 0
      ? filteredSilobags.filter(
          (item: SilobagListType) => item?.countryside_id == filtersReportDevices.countrysideId
        )
      : [];

  const removeFilter = () => {
    setDeviceName('');
    // Estado Global
    setFiltersReportDevices({
      lance_number: '',
      countrysideId: '',
      silobagId: '',
      report: '',
      installed: '',
      paginationModel: {
        page: 0,
        pageSize: 50,
      },
    });
  };

  const silobagRender = siloByCountrysideId !== undefined ? siloByCountrysideId : filteredSilobags;

  const deleteCountrysideFilter = () => {
    setFiltersReportDevices({ ...filtersReportDevices, countrysideId: '' });
  };

  const deleteSilobagFilter = () => {
    setFiltersReportDevices({ ...filtersReportDevices, silobagId: '' });
  };

  return (
    <Box style={{ margin: 'auto', maxWidth: '95%' }}>
      {deviceLoading && silobagsLoading && countrysidesLoading ? (
        <Box sx={boxCircularProgressBarStyle}>
          <CircularProgress sx={circularProgressBar} size={50} />
        </Box>
      ) : (
        <Box>
          <Box component="h1" fontWeight={500} style={{ color: '#49454F' }}>
            {i18n.t('app.report_devices.devices_reports_title')}
          </Box>
          {deviceReportData && (
            <TotalsDevices
              total={deviceReportData?.filteredRowCount}
              installed={deviceReportData?.installed}
              uninstalled={deviceReportData?.uninstalled}
              reporting={deviceReportData?.reporting}
              notReporting={deviceReportData?.not_reporting}
            />
          )}
          <section
            id="filters"
            style={{
              marginTop: '2rem',
              display: 'flex',
            }}
          >
            <Stack direction={'row'} spacing={2} width="100%">
              <TextField
                value={filtersReportDevices.lance_number}
                size="medium"
                style={{ backgroundColor: '#fff', flex: 1 }}
                label={i18n.t('app.report_devices.search_device_name')}
                onChange={handleSearchDevice}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={SearchIcon} alt="search-icon" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="medium" style={{ flex: 1 }}>
                <InputLabel>{i18n.t('app.report_devices.status')}</InputLabel>
                <Select
                  style={{ border: 'none', backgroundColor: '#fff' }}
                  label={i18n.t('app.report_devices.status')}
                  onChange={handleSelectSilobagInstalled}
                  value={(filtersReportDevices.installed as boolean) ?? ''}
                >
                  <MenuItem value="">
                    <Typography>{i18n.t('app.report_devices.status')}</Typography>
                  </MenuItem>
                  <MenuItem value="true">
                    <Chip
                      size="small"
                      label={i18n.t('app.report_devices.installed')}
                      style={{
                        gap: '10px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(0, 123, 255, 0.10)',
                        color: '#22A7F0',
                      }}
                    />
                  </MenuItem>
                  <MenuItem value="false">
                    <Chip
                      label={i18n.t('app.report_devices.not_installed')}
                      size="small"
                      color="default"
                      style={{ borderRadius: '10px' }}
                    />
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl size="medium" style={{ flex: 1 }}>
                <InputLabel>{i18n.t('app.report_devices.report')}</InputLabel>
                <Select
                  style={{ border: 'none', backgroundColor: '#fff' }}
                  label={i18n.t('app.report_devices.report')}
                  onChange={handleSelectReport}
                  value={(filtersReportDevices.report as boolean) ?? ''}
                >
                  <MenuItem value="true">
                    <Typography>{i18n.t('app.report_devices.report')}</Typography>
                  </MenuItem>
                  <MenuItem value="true">
                    <Chip
                      label={i18n.t('app.report_devices.reporting')}
                      size="small"
                      style={{
                        borderRadius: '10px',
                        backgroundColor: 'rgba(107, 170, 0, 0.15)',
                        color: '#73BD00',
                      }}
                    />
                  </MenuItem>
                  <MenuItem value="false">
                    <Chip
                      label={i18n.t('app.report_devices.delayed')}
                      size="small"
                      style={{
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 0, 0, 0.1',
                        color: '#FF0000',
                      }}
                    />
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl size="medium" style={{ flex: 1 }}>
                <Selector
                  label={i18n.t('app.countrysides')}
                  id="countryside_id"
                  value={filtersReportDevices.countrysideId ?? ''}
                  onChange={(e) => handleSelectCountryside(e as SelectChangeEvent<number>)}
                  options={filteredCountrysides}
                  selectedList={filtersReportDevices.countrysideId}
                  renderValue={() =>
                    filteredCountrysides
                      ?.filter((countryside) => filtersReportDevices.countrysideId == countryside?.id)
                      .map((matchedCountryside) => matchedCountryside?.name)
                  }
                  deleteFilter={deleteCountrysideFilter}
                  disabled={countrysidesLoading}
                />
              </FormControl>
              <FormControl size="medium" style={{ flex: 1 }}>
                <Selector
                  label={i18n.t('app.silobags')}
                  id="silobag_id"
                  value={filtersReportDevices.silobagId}
                  onChange={(e) => handleSelectSilobag(e as SelectChangeEvent)}
                  options={silobagRender}
                  selectedList={filtersReportDevices.silobagId}
                  renderValue={() =>
                    silobagRender
                      ?.filter((silobag) => filtersReportDevices.silobagId == silobag?.id)
                      .map((matchedSilobag) => matchedSilobag?.name)
                  }
                  deleteFilter={deleteSilobagFilter}
                  disabled={silobagsLoading || !filtersReportDevices.countrysideId}
                />
              </FormControl>
              <Button
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  paddingInline: 2,
                  textTransform: 'uppercase',
                  visibility: hasFilters ? '' : 'hidden',
                  color: '#FF0000',
                  '&:hover': { background: '#FF000022' },
                }}
                onClick={removeFilter}
                startIcon={<img src={DeleteIcon} alt="delete-icon" style={{ width: 14, height: 14 }} />}
              >
                {i18n.t('app.clear_filters')}
              </Button>
            </Stack>
          </section>
          <DashboardDevices
            devices={deviceReportData?.devices}
            paginationModel={filtersReportDevices?.paginationModel}
            setPaginationModel={(fn) => {
              const paginationModel = fn(filtersReportDevices.paginationModel);
              setFiltersReportDevices({ ...filtersReportDevices, paginationModel });
            }}
            isLoading={deviceLoading}
            rowCount={deviceReportData?.filteredRowCount ?? 0}
          />
        </Box>
      )}
    </Box>
  );
}
